import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { getAuthLocalStorage } from '../../../storage/localStorage';
import { NotificationApiService } from '../../../service/notificationApiService';
import { SettingApiService } from '../../../service/seller/settingApiService';
import { imPointsApiService } from '../../../service/admin/imPointsApiService';
import { AuthApiService } from '../../../service/authApiService';
import { ToastTypes, useMessage } from '../../../base/context/MessageProvider';
import {
  FaBars, FaSearch, FaBell, FaMoon, FaSun, FaChevronDown,
  FaUser, FaCog, FaSignOutAlt, FaWallet, FaRupeeSign, FaSyncAlt, FaCoins
} from 'react-icons/fa';
import './sellerHeader.css';

// Page title mapping for seller routes
const pageTitles = {
  '/seller/dashboard': 'Dashboard',
  '/seller/orders': 'Orders',
  '/seller/orders/failed': 'Failed Orders',
  '/seller/amazon-product': 'Search Products',
  // '/seller/flipkart-product': 'Search Products',
  '/seller/find-similar': 'Find Similar Products',
  '/seller/product-detail': 'Product Detail',
  '/seller/marketplaceApis': 'Store APIs',
  '/seller/subscription': 'Subscription',
  '/seller/productSubscription': 'Product Subscriptions',
  '/seller/wallet': 'IM Cash',
  '/seller/settings': 'Settings',
  '/seller/reports/invoice': 'Invoices',
  '/seller/profile': 'Profile',
  '/seller/affiliate/dashboard': 'Affiliate Dashboard',
  '/seller/affiliate/sellers': 'My Sellers',
  '/seller/affiliate/earnings': 'Earnings',
  '/seller/affiliate/withdraw': 'Withdraw',
  '/seller/affiliate/profile': 'Affiliate Profile',
};

export default function SellerHeader({ onMenuClick, darkMode, setDarkMode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const message = useMessage();
  
  const [userData, setUserData] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [walletLoading, setWalletLoading] = useState(false);
  const [imPoints, setImPoints] = useState(0);
  const [imPointsLoading, setImPointsLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unseenCount, setUnseenCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  
  const notifRef = useRef(null);
  const profileRef = useRef(null);

  useEffect(() => {
    const auth = getAuthLocalStorage();
    if (auth && auth.isLoggedIn) {
      // Fetch user profile to get first name and last name
      AuthApiService.getProfile()
        .then((res) => {
          if (res?.status?.code === 200 && res?.data) {
            const firstName = res.data.firstName || '';
            const lastName = res.data.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim() || 'Seller';
            setUserData({
              name: fullName,
              role: auth.userType || 'seller',
              email: res.data.email || '',
            });
          } else {
            setUserData({
              name: 'Seller',
              role: auth.userType || 'seller',
              email: '',
            });
          }
        })
        .catch(() => {
          setUserData({
            name: 'Seller',
            role: auth.userType || 'seller',
            email: '',
          });
        });
      fetchNotifications();
      fetchWalletBalance();
      fetchIMPoints();
    }
  }, []);

  const fetchIMPoints = async (showToast = false) => {
    setImPointsLoading(true);
    try {
      const res = await imPointsApiService.getUserIMPoints();
      if (res?.status?.code === 200) {
        setImPoints(res?.data?.points || 0);
        if (showToast) {
          message.showToast('IM Points refreshed', ToastTypes.SUCCESS);
        }
      }
    } catch (err) {
      console.error('Failed to fetch IM Points:', err);
      if (showToast) {
        message.showToast('Failed to refresh IM Points', ToastTypes.ERROR);
      }
    }
    setImPointsLoading(false);
  };

  const fetchWalletBalance = async (showToast = false) => {
    setWalletLoading(true);
    try {
      const res = await SettingApiService.getDashboard();
      if (res?.status?.code === 200) {
        setWalletBalance(res?.data?.walletBalance || 0);
        if (showToast) {
          message.showToast('IM Cash balance refreshed', ToastTypes.SUCCESS);
        }
      }
    } catch (err) {
      console.error('Failed to fetch IM Cash balance:', err);
      if (showToast) {
        message.showToast('Failed to refresh IM Cash', ToastTypes.ERROR);
      }
    }
    setWalletLoading(false);
  };

  const formatWalletBalance = (value) => {
    if (!value) return '₹0.00';
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (profileRef.current && !profileRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchNotifications = async () => {
    try {
      const countRes = await NotificationApiService.getUnseenCount();
      if (countRes?.status?.code === 200) {
        setUnseenCount(countRes?.data?.count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const openNotifications = async () => {
    setShowNotifications(!showNotifications);
    setShowProfileMenu(false);
    
    if (!showNotifications) {
      try {
        const res = await NotificationApiService.getNotifications('?page=1&limit=10');
        if (res?.status?.code === 200) {
          setNotifications(res?.data?.notifications || []);
          await NotificationApiService.markAllSeen();
          setUnseenCount(0);
        }
      } catch (err) {
        console.error('Failed to fetch notifications:', err);
      }
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/login');
  };

  const getPageTitle = () => {
    const path = location.pathname;
    // Check for exact match first
    if (pageTitles[path]) return pageTitles[path];
    // Check for partial matches (for dynamic routes)
    for (const [route, title] of Object.entries(pageTitles)) {
      if (path.startsWith(route)) return title;
    }
    return 'Seller Panel';
  };

  const getInitials = (name) => {
    if (!name) return 'S';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const formatTimeAgo = (date) => {
    if (!date) return '';
    const now = new Date();
    const diff = now - new Date(date);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      // Navigate to product search with query
      navigate(`/seller/amazon-product?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <header className="seller-admin-header">
      {/* Left Section */}
      <div className="seller-header-left">
        <button className="seller-header-menu-btn" onClick={onMenuClick}>
          <FaBars />
        </button>
        
        <h1 className="seller-header-title">{getPageTitle()}</h1>
      </div>

      {/* Right Section */}
      <div className="seller-header-right">
        {/* IM Cash Balance */}
        <div className="seller-header-wallet">
          <div className="seller-header-wallet-info" onClick={() => navigate('/seller/wallet')}>
            <FaWallet className="seller-header-wallet-icon" />
            <span className="seller-header-wallet-amount">{formatWalletBalance(walletBalance)}</span>
          </div>
          <button 
            className={`seller-header-wallet-refresh ${walletLoading ? 'seller-spin' : ''}`}
            onClick={() => fetchWalletBalance(true)}
            title="Refresh balance"
          >
            <FaSyncAlt />
          </button>
        </div>

        {/* IM Points */}
        <div className="seller-header-wallet" style={{ background: "#eef2ff", border: "1px solid #c7d2fe" }}>
          <div className="seller-header-wallet-info" onClick={() => navigate('/seller/wallet')}>
            <FaCoins className="seller-header-wallet-icon" style={{ color: "#4f46e5" }} />
            <span className="seller-header-wallet-amount" style={{ color: "#4f46e5", fontWeight: 600 }}>{imPoints.toLocaleString()} Points</span>
          </div>
          <button 
            className={`seller-header-wallet-refresh ${imPointsLoading ? 'seller-spin' : ''}`}
            onClick={() => fetchIMPoints(true)}
            title="Refresh IM Points"
          >
            <FaSyncAlt />
          </button>
        </div>

        {/* Action Buttons */}
        <div className="seller-header-actions">
          {/* Dark Mode Toggle - Hidden for now */}
          {/* <button 
            className="seller-header-action-btn"
            onClick={() => setDarkMode && setDarkMode(!darkMode)}
            title={darkMode ? 'Light Mode' : 'Dark Mode'}
          >
            {darkMode ? <FaSun /> : <FaMoon />}
          </button> */}

          {/* Notifications */}
          <div ref={notifRef} style={{ position: 'relative' }}>
            <button 
              className="seller-header-action-btn"
              onClick={openNotifications}
            >
              <FaBell />
              {unseenCount > 0 && (
                <span className="seller-header-action-badge">{unseenCount > 99 ? '99+' : unseenCount}</span>
              )}
            </button>

            {showNotifications && (
              <div className="seller-notification-dropdown">
                <div className="seller-notification-header">
                  <span className="seller-notification-title">Notifications</span>
                  <button className="seller-notification-mark-read">Mark all read</button>
                </div>
                
                <div className="seller-notification-list">
                  {notifications.length === 0 ? (
                    <div className="seller-notification-empty">
                      No notifications yet
                    </div>
                  ) : (
                    notifications.map((notif, index) => (
                      <div 
                        key={index} 
                        className={`seller-notification-item ${!notif.seen ? 'seller-unread' : ''}`}
                        onClick={() => {
                          if (notif.link) navigate(notif.link);
                          setShowNotifications(false);
                        }}
                      >
                        <div className="seller-notification-item-title">{notif.title}</div>
                        <div className="seller-notification-item-desc">{notif.message}</div>
                        <div className="seller-notification-item-time">{formatTimeAgo(notif.createdAt)}</div>
                      </div>
                    ))
                  )}
                </div>
                
                <div className="seller-notification-footer">
                  <Link to="/seller/orders" className="seller-notification-view-all">
                    View all orders
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* User Profile */}
        <div ref={profileRef} style={{ position: 'relative' }}>
          <div 
            className={`seller-header-profile ${showProfileMenu ? 'seller-dropdown-open' : ''}`}
            onClick={() => {
              setShowProfileMenu(!showProfileMenu);
              setShowNotifications(false);
            }}
          >
            <div className="seller-header-avatar">
              {getInitials(userData?.name)}
            </div>
            <div className="seller-header-user-info">
              <span className="seller-header-user-name">{userData?.name}</span>
              <span className="seller-header-user-role">{userData?.role}</span>
            </div>
            <FaChevronDown className="seller-header-dropdown-arrow" />
          </div>

          {showProfileMenu && (
            <div className="seller-profile-dropdown">
              <div className="seller-profile-dropdown-header">
                <div className="seller-profile-dropdown-avatar">
                  {getInitials(userData?.name)}
                </div>
                <div className="seller-profile-dropdown-info">
                  <div className="seller-profile-dropdown-name">{userData?.name}</div>
                  <div className="seller-profile-dropdown-email">{userData?.email}</div>
                </div>
              </div>
              <div className="seller-profile-dropdown-divider" />
              <Link to="/seller/profile" className="seller-profile-dropdown-item" onClick={() => setShowProfileMenu(false)}>
                <FaUser className="seller-profile-dropdown-icon" />
                My Profile
              </Link>
              <Link to="/seller/wallet" className="seller-profile-dropdown-item" onClick={() => setShowProfileMenu(false)}>
                <FaWallet className="seller-profile-dropdown-icon" />
                IM Cash
              </Link>
              <Link to="/seller/settings" className="seller-profile-dropdown-item" onClick={() => setShowProfileMenu(false)}>
                <FaCog className="seller-profile-dropdown-icon" />
                Settings
              </Link>
              <div className="seller-profile-dropdown-divider" />
              <button className="seller-profile-dropdown-item seller-logout" onClick={handleLogout}>
                <FaSignOutAlt className="seller-profile-dropdown-icon" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
